import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { Commands, Search } from '@local/client-contracts';
import { PopupService } from '@local/ui-infra';
import { ContextMenuService } from '@shared/components';
import { EventInfo, EventsService, LogService } from '@shared/services';
import { ApplicationsService } from '@shared/services/applications.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { FlagsService } from '@shared/services/flags.service';
import { KeyboardService } from '@shared/services/keyboard.service';
import { ResourcesService } from '@shared/services/resources.service';
import { RouterService } from '@shared/services/router.service';
import { KeyName, normalizeKeys } from '@local/ts-infra';
import { firstValueFrom } from 'rxjs';
import { CommandBarService } from 'src/app/bar/services/command-bar.service';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { ResultsService } from 'src/app/bar/services/results.service';
import { GlobalErrorHandler } from 'src/app/global-error-handler';
import { TelemetryTrigger } from '../../../results/models/results-types';
import { ResourceViewItemComponent } from '../../view-item/resource-view-item.component';
import { MailBuildResultViewService, MailViewModel } from '../mail-build-result-view.service';
import { MailCommandBuilderService } from '../mail-command-builder.service';
import { MailContextMenuHelper } from '../mail-context-menu-helper';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { WorkspacesService } from 'src/app/bar/services';
import { SearchPopUpService } from '../../../special-views/search-popup/search-popup.service';

@Component({
  selector: 'mail-view-item',
  templateUrl: './mail-view-item.component.html',
  styleUrls: ['./mail-view-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MailViewItemComponent extends ResourceViewItemComponent implements AfterViewInit {
  @Input() set model(value: Search.ResultResourceItem) {
    if (!value) return;
    super.model = value;
    this.viewModel = this.mailBuildResultViewService.buildResultView(value, this.inboxFilterOnly);
    this.viewModel.sender.tooltip = this.viewModel.senderEmail;
    this.cdr.markForCheck();
  }
  @Input() inboxFilterOnly: boolean;
  @Input() preview: boolean = false;

  @ViewChild('senderContainer') senderContainer: ElementRef;
  @ViewChild('senderName') senderName: ElementRef;

  get model() {
    return super.model;
  }

  viewModel: MailViewModel;

  constructor(
    hostRef: ElementRef,
    protected cdr: ChangeDetectorRef,
    logService: LogService,
    protected applicationsService: ApplicationsService,
    contextMenuService: ContextMenuService,
    mailCommandBuilder: MailCommandBuilderService,
    protected hubService: HubService,
    eventsService: EventsService,
    downloadsService: DownloadsService,
    resourcesService: ResourcesService,
    commandsService: CommandsService,
    commandBarService: CommandBarService,
    popupService: PopupService,
    keyboardService: KeyboardService,
    routerService: RouterService,
    errorHandler: GlobalErrorHandler,
    resultsService: ResultsService,
    private mailBuilder: MailCommandBuilderService,
    private filtersService: FiltersService,
    protected flagsService: FlagsService,
    private mailBuildResultViewService: MailBuildResultViewService,
    protected favoritesService: FavoritesService,
    protected blobsService: BlobsService,
    protected workspacesService: WorkspacesService,
    protected searchPopUpService: SearchPopUpService
  ) {
    super(
      hostRef,
      cdr,
      logService,
      applicationsService,
      contextMenuService,
      mailCommandBuilder,
      hubService,
      eventsService,
      downloadsService,
      resourcesService,
      commandsService,
      commandBarService,
      popupService,
      keyboardService,
      routerService,
      errorHandler,
      resultsService,
      flagsService,
      favoritesService,
      blobsService,
      workspacesService,
      searchPopUpService
    );
  }

  ngAfterViewInit(): void {
    this.contextMenuHelper = this.initContextMenuHelper();
    this.cdr.markForCheck();
    super.ngAfterViewInit();
  }

  async setShortcutsHandlers() {
    await super.setShortcutsHandlers();
    const copyEmail = await firstValueFrom(this.keyboardService.get('_mail_copyEmail'));
    let keys: string[];

    if (copyEmail) {
      keys = normalizeKeys(<string[]>copyEmail.keys).map((k) => k.toLowerCase());
      if (keys) {
        this.shortcutsHandlers.push(this.createNewItem(keys as KeyName[], 'email'));
      }
    }
  }

  private createNewItem(keys: KeyName[], trait: string) {
    return {
      fn: (item: MailViewItemComponent, trigger: TelemetryTrigger, eventInfo: any) => {
        item.copyBullet(trigger, eventInfo, trait);
      },
      keyBinding: keys as KeyName[],
    };
  }

  private copyBullet(trigger: TelemetryTrigger, eventInfo: Partial<EventInfo>, traitName: string): boolean {
    const isTextSelected = document.getSelection() && document.getSelection().toString() !== '';
    const trait = traitName === 'email' ? this.model.resource.traits.sender.email : this.model.resource.traits[traitName];
    if (!isTextSelected && trait) {
      const command: Commands.CopyClipboard = { type: 'copy-clipboard', value: trait };
      this.onInvoke({ command, trigger, eventInfo });
      return true;
    }
    return false;
  }

  protected initContextMenuHelper(): MailContextMenuHelper {
    return new MailContextMenuHelper(
      this.mailBuilder,
      this.logger,
      this.contextMenuService,
      this.hostRef,
      this.titleRef,
      this.subtitleRef,
      this.actionsRef,
      this.errorHandler,
      this.resourcesService,
      this.hubService,
      this.flagsService,
      this.workspacesService
    );
  }
}
